import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import type {
  DiagnosticChannel,
  DiagnosticView,
  DiagnosticRequest,
  DiagnosticState,
} from "@reminder-tracker/backend-contracts";
import {
  getDeliveryCapabilities,
  getDiagnosticView,
  requestDiagnosticTest,
} from "../../backend/functions";
import { exactUtcTimestamp, formatAdministrationTime } from "../../ui/administration-time";
import {
  AdministrationAuditDetails,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

const stateLabel: Readonly<Record<DiagnosticState, string>> = {
  pending: "Request recorded — waiting to dispatch",
  processing: "Provider request in progress",
  accepted: "Provider accepted the request",
  failed: "Provider request failed",
  unknown: "Outcome unknown — do not resend",
  cancelled: "Cancelled before provider dispatch",
  expired: "Diagnostic evidence expired",
};
const label = (channel: DiagnosticChannel) => (channel === "email" ? "Email" : "Android push");
const opaque = (prefix: string) => `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;

export function ProtectedDeliveryDiagnostics(): React.JSX.Element {
  const [correlationId] = useState(() => opaque("corr"));
  const capability = useQuery(getDeliveryCapabilities, { correlationId });
  const permitted = capability?.canSendDiagnostic === true;
  return (
    <AdministrationWorkSurface
      id="delivery-diagnostics-title"
      title="Send and review an operations test"
      purpose="Check eligibility, send one bounded test, then review its recorded outcome in the same work surface."
      state={{
        label:
          capability === undefined
            ? "Checking permission…"
            : permitted
              ? "Testing available"
              : "Permission required",
        tone: capability === undefined ? "neutral" : permitted ? "healthy" : "monitor",
      }}
      consequence="Tests contain no customer information and never certify inbox receipt, phone display or reading."
    >
      {capability === undefined ? (
        <p role="status">Checking test permission…</p>
      ) : !permitted ? (
        <p>Sending a test requires the delivery-test permission.</p>
      ) : (
        <>
          <ConnectedDiagnostic channel="email" />
          <ConnectedDiagnostic channel="push" />
        </>
      )}
    </AdministrationWorkSurface>
  );
}
function ConnectedDiagnostic({
  channel,
}: Readonly<{ channel: DiagnosticChannel }>): React.JSX.Element {
  const [correlationId, setCorrelationId] = useState(() => opaque("corr"));
  useEffect(() => {
    const timer = globalThis.setInterval(() => {
      if (document.visibilityState === "visible") setCorrelationId(opaque("corr"));
    }, 60_000);
    return () => globalThis.clearInterval(timer);
  }, []);
  const view = useQuery(getDiagnosticView, { correlationId, channel });
  const request = useMutation(requestDiagnosticTest);
  const retained = useRef<DiagnosticRequest | null>(null);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  if (view === undefined)
    return <p role="status">Reading {label(channel).toLowerCase()} test status…</p>;
  async function confirm(destinationRevision: number, policyRevision: number) {
    if (pending) return;
    const previous = retained.current;
    const command =
      previous !== null &&
      previous.destinationRevision === destinationRevision &&
      previous.policyRevision === policyRevision
        ? previous
        : {
            commandKey: opaque("cmd"),
            correlationId: opaque("corr"),
            channel,
            destinationRevision,
            policyRevision,
            issuedAtEpochMilliseconds: Date.now(),
          };
    retained.current = command;
    setPending(true);
    setFeedback(null);
    try {
      const result = await request(command);
      retained.current = null;
      setFeedback(`${result.replayed ? "Existing test: " : ""}${stateLabel[result.state]}.`);
    } catch {
      setFeedback(
        "The test was not confirmed. Check the latest status before retrying; the same request reference will be reused.",
      );
    } finally {
      setPending(false);
    }
  }
  return (
    <DiagnosticReview
      channel={channel}
      view={view}
      pending={pending}
      feedback={feedback}
      onConfirm={(destinationRevision, policyRevision) =>
        void confirm(destinationRevision, policyRevision)
      }
    />
  );
}
export function DiagnosticReview({
  channel,
  view,
  pending,
  feedback,
  onConfirm,
}: Readonly<{
  channel: DiagnosticChannel;
  view: DiagnosticView;
  pending: boolean;
  feedback: string | null;
  onConfirm: (destinationRevision: number, policyRevision: number) => void;
}>): React.JSX.Element {
  const [review, setReview] = useState<{
    destinationRevision: number;
    policyRevision: number;
  } | null>(null);
  const unresolved = view.recent.some(
    (row) => row.state === "unknown" || row.state === "processing" || row.state === "pending",
  );
  const unavailable =
    view.destination === null ||
    view.policyMode !== "test-only" ||
    view.policyRevision === null ||
    !view.providerConfigured ||
    unresolved;
  const stale =
    review !== null &&
    (review.destinationRevision !== view.destination?.revision ||
      review.policyRevision !== view.policyRevision);
  return (
    <section className="delivery-diagnostic" aria-label={`${label(channel)} diagnostic`}>
      <span className="decision-state">
        {unresolved
          ? "Wait for the current outcome"
          : unavailable
            ? "Test unavailable"
            : "Ready for review"}
      </span>
      <h3>{label(channel)} test</h3>
      <p>
        {view.environment} ·{" "}
        {view.destination?.label ?? "No verified operations destination is available"}
      </p>
      {view.policyMode !== "test-only" ? (
        <p>This channel is disabled. A test cannot bypass its policy.</p>
      ) : null}
      {!view.providerConfigured ? <p>Required provider settings are missing.</p> : null}
      {unresolved ? (
        <p>An earlier test is pending or unresolved. Do not send another test.</p>
      ) : null}
      <button
        className="secondary-action"
        type="button"
        disabled={pending || unavailable}
        onClick={() => {
          if (view.destination !== null && view.policyRevision !== null)
            setReview({
              destinationRevision: view.destination.revision,
              policyRevision: view.policyRevision,
            });
        }}
      >
        Review {label(channel).toLowerCase()} test
      </button>
      {review === null ? null : (
        <div
          className="decision-review"
          role="group"
          aria-label={`Confirm ${label(channel).toLowerCase()} test`}
        >
          <p>
            Send one fixed operations test to{" "}
            {view.destination?.label ?? "the unavailable destination"} in {view.environment}?
          </p>
          <p>
            The message contains no customer or reminder information. Provider acceptance does not
            prove inbox receipt, phone display or reading.
          </p>
          <p>
            Limits: one new test per channel every five minutes, five per destination per hour, and
            ten per environment per hour.
          </p>
          {stale ? (
            <p role="alert">The policy or destination changed. Cancel and review again.</p>
          ) : null}
          <button
            className="primary-action"
            type="button"
            disabled={pending || unavailable || stale}
            onClick={() => {
              if (unavailable || stale) return;
              onConfirm(review.destinationRevision, review.policyRevision);
              setReview(null);
            }}
          >
            Send one {label(channel).toLowerCase()} test
          </button>
          <button
            className="secondary-action"
            type="button"
            disabled={pending}
            onClick={() => setReview(null)}
          >
            Keep test unsent
          </button>
        </div>
      )}
      {feedback === null ? null : <p role="status">{feedback}</p>}
      <AdministrationAuditDetails summary="Recent test outcomes and exact recorded times">
        {view.recent.length === 0 ? (
          <p>No retained operations tests.</p>
        ) : (
          <ul>
            {view.recent.map((row) => (
              <li key={row.diagnosticId}>
                {stateLabel[row.state]} ·{" "}
                <time dateTime={exactUtcTimestamp(row.createdAtEpochMilliseconds)}>
                  {formatAdministrationTime(row.createdAtEpochMilliseconds)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </AdministrationAuditDetails>
    </section>
  );
}
